import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BLOG_POSTS } from '../data/blogPosts';
import { WRAP, SECTION_PAD } from '../styles/layout';

function useBreakpoint() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return { mob: w < 768, tab: w < 1024 };
}

const BG   = '#09060F';
const C    = '#00D4AA';
const R    = '0,212,170';
const CP   = '#7C3AED';
const RP   = '124,58,237';
const LAV  = '#EDE9F8';
const LAV2 = '#C8C0EC';
const LAV3 = '#9B90BA';

export default function Blog() {
  const navigate = useNavigate();
  const { mob, tab } = useBreakpoint();
  const featured = BLOG_POSTS.find(p => p.featured);
  const rest     = BLOG_POSTS.filter(p => !p.featured);

  return (
    <>
      <Helmet>
        <title>Blog — RevUpAI | AI Governance, Database Migration & Enterprise AI</title>
        <meta name="description" content="Practical guides on AI governance, shadow AI discovery, NIST AI RMF, database migration, and enterprise AI deployment from the RevUpAI team." />
        <meta name="keywords" content="AI governance blog, shadow AI, NIST AI RMF, data modernisation AI, legacy database migration, enterprise AI, AI CISO" />
        <link rel="canonical" href="https://revupai.com/blog" />
        <meta property="og:title" content="Blog — RevUpAI | AI Governance & Enterprise AI" />
        <meta property="og:description" content="Practical guides on AI governance, shadow AI discovery, and enterprise AI deployment." />
        <meta property="og:url" content="https://revupai.com/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://revupai.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:none} }
        * { box-sizing: border-box; }
      `}</style>

      <div style={{ background:BG, minHeight:'100vh', paddingTop:80 }}>

        {/* ── Hero ── */}
        <div style={{ borderBottom:`1px solid rgba(255,255,255,.06)`, padding:`clamp(48px,6vh,72px) 0 clamp(40px,5vh,56px)` }}>
          <div style={{ ...WRAP, maxWidth:1100 }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'5px 14px', borderRadius:100, background:`rgba(${R},.08)`, border:`1px solid rgba(${R},.22)`, marginBottom:20 }}>
              <div style={{ width:6, height:6, borderRadius:'50%', background:C }}/>
              <span style={{ fontSize:11, fontWeight:700, letterSpacing:2.5, textTransform:'uppercase', color:C, fontFamily:"'DM Sans',system-ui,sans-serif" }}>RevUpAI Blog</span>
            </div>
            <h1 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:'clamp(36px,4vw,60px)', color:LAV, margin:'0 0 16px', letterSpacing:'-2px', lineHeight:1.06 }}>
              Insights for the<br/>
              <span style={{ color:C }}>enterprise AI era.</span>
            </h1>
            <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:'clamp(14px,1.1vw,16px)', fontWeight:300, color:LAV2, maxWidth:520, lineHeight:1.8, margin:0 }}>
              Practical guides on AI governance, shadow AI discovery, database migration, and deploying AI agents that actually do the job.
            </p>
          </div>
        </div>

        <div style={{ ...WRAP, maxWidth:1100, padding:`clamp(48px,6vh,72px) clamp(20px,4vw,40px)` }}>

          {/* ── Featured post ── */}
          {featured && (
            <div style={{ marginBottom:64 }}>
              <div style={{ fontSize:10, fontWeight:700, letterSpacing:3, textTransform:'uppercase', color:LAV3, marginBottom:20, fontFamily:"'DM Sans',system-ui,sans-serif" }}>Featured</div>
              <div onClick={() => navigate(`/blog/${featured.slug}`)}
                style={{ display:'grid', gridTemplateColumns: tab ? '1fr' : '1fr 1fr', gap: tab ? 28 : 48, padding: mob ? '24px' : '40px 44px', background:'rgba(255,255,255,.025)', border:`1px solid rgba(${featured.categoryRgb},.2)`, borderRadius:16, cursor:'pointer', transition:'all .25s ease', position:'relative', overflow:'hidden' }}
                onMouseEnter={e => { e.currentTarget.style.background=`rgba(${featured.categoryRgb},.05)`; e.currentTarget.style.borderColor=`rgba(${featured.categoryRgb},.4)`; e.currentTarget.style.transform='translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,.025)'; e.currentTarget.style.borderColor=`rgba(${featured.categoryRgb},.2)`; e.currentTarget.style.transform='none'; }}
              >
                <div style={{ position:'absolute', top:-60, right:-60, width:300, height:300, borderRadius:'50%', background:`radial-gradient(circle,rgba(${featured.categoryRgb},.08) 0%,transparent 70%)`, pointerEvents:'none' }}/>

                <div style={{ display:'flex', flexDirection:'column', justifyContent:'space-between' }}>
                  <div>
                    <div style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 12px', borderRadius:100, background:`rgba(${featured.categoryRgb},.1)`, border:`1px solid rgba(${featured.categoryRgb},.25)`, marginBottom:20 }}>
                      <span style={{ fontSize:11, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:featured.categoryColor, fontFamily:"'DM Sans',system-ui,sans-serif" }}>{featured.category}</span>
                    </div>
                    <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:'clamp(22px,2vw,30px)', color:LAV, margin:'0 0 16px', letterSpacing:'-0.5px', lineHeight:1.2 }}>{featured.title}</h2>
                    <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:15, fontWeight:300, color:LAV2, lineHeight:1.8, margin:0 }}>{featured.excerpt}</p>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:16, marginTop:28 }}>
                    <span style={{ fontSize:12, color:LAV3, fontFamily:"'DM Sans',system-ui,sans-serif" }}>{featured.date}</span>
                    <span style={{ width:3, height:3, borderRadius:'50%', background:LAV3 }}/>
                    <span style={{ fontSize:12, color:LAV3, fontFamily:"'DM Sans',system-ui,sans-serif" }}>{featured.readTime}</span>
                  </div>
                </div>

                <div style={{ display:'flex', flexDirection:'column', justifyContent:'center' }}>
                  <div style={{ padding:'28px', background:`rgba(${featured.categoryRgb},.06)`, borderRadius:12, border:`1px solid rgba(${featured.categoryRgb},.15)`, width:'100%' }}>
                    <div style={{ fontSize:11, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:`rgba(${featured.categoryRgb},.7)`, marginBottom:16, fontFamily:"'DM Sans',system-ui,sans-serif" }}>What you'll learn</div>
                    {[
                      'Where shadow AI actually hides in your enterprise',
                      'The 4-layer discovery framework',
                      'How to brief your board with real numbers',
                      'Day 1 governance actions any CISO can take',
                    ].map((item, i) => (
                      <div key={i} style={{ display:'flex', gap:10, marginBottom:12, alignItems:'flex-start' }}>
                        <div style={{ width:5, height:5, borderRadius:'50%', background:featured.categoryColor, marginTop:6, flexShrink:0 }}/>
                        <span style={{ fontSize:13, fontWeight:300, color:LAV2, lineHeight:1.6, fontFamily:"'DM Sans',system-ui,sans-serif" }}>{item}</span>
                      </div>
                    ))}
                    <div style={{ marginTop:20, display:'inline-flex', alignItems:'center', gap:8, color:featured.categoryColor, fontSize:13, fontWeight:600, fontFamily:"'DM Sans',system-ui,sans-serif" }}>
                      Read article <span>→</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Other posts — live + coming soon ── */}
          <div style={{ display:'grid', gridTemplateColumns: tab ? '1fr' : 'repeat(auto-fit,minmax(300px,1fr))', gap:20 }}>
            {rest.map((post, i) => (
              <div key={i}
                onClick={() => !post.comingSoon && navigate(`/blog/${post.slug}`)}
                style={{ padding: mob ? '20px' : '28px', background:'rgba(255,255,255,.02)', border:`1px solid rgba(${post.categoryRgb},.15)`, borderRadius:12, opacity: post.comingSoon ? .6 : 1, position:'relative', overflow:'hidden', cursor: post.comingSoon ? 'default' : 'pointer', transition:'all .2s ease' }}
                onMouseEnter={e => { if (!post.comingSoon) { e.currentTarget.style.background=`rgba(${post.categoryRgb},.05)`; e.currentTarget.style.borderColor=`rgba(${post.categoryRgb},.3)`; e.currentTarget.style.transform='translateY(-2px)'; }}}
                onMouseLeave={e => { if (!post.comingSoon) { e.currentTarget.style.background='rgba(255,255,255,.02)'; e.currentTarget.style.borderColor=`rgba(${post.categoryRgb},.15)`; e.currentTarget.style.transform='none'; }}}
              >
                {post.comingSoon && (
                  <div style={{ position:'absolute', top:14, right:14, padding:'3px 10px', borderRadius:100, background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.1)' }}>
                    <span style={{ fontSize:9, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:LAV3, fontFamily:"'DM Sans',system-ui,sans-serif" }}>Coming Soon</span>
                  </div>
                )}
                <div style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 12px', borderRadius:100, background:`rgba(${post.categoryRgb},.08)`, border:`1px solid rgba(${post.categoryRgb},.2)`, marginBottom:16 }}>
                  <span style={{ fontSize:11, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:post.categoryColor, fontFamily:"'DM Sans',system-ui,sans-serif" }}>{post.category}</span>
                </div>
                <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:700, fontSize:'clamp(17px,1.4vw,21px)', color:LAV, margin:'0 0 12px', letterSpacing:'-0.3px', lineHeight:1.3 }}>{post.title}</h3>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, fontWeight:300, color:LAV2, lineHeight:1.75, margin:'0 0 20px' }}>{post.excerpt}</p>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <span style={{ fontSize:12, color:`rgba(${post.categoryRgb},.6)`, fontFamily:"'DM Sans',system-ui,sans-serif" }}>{post.readTime}</span>
                  {!post.comingSoon && <span style={{ fontSize:12, color:post.categoryColor, fontFamily:"'DM Sans',system-ui,sans-serif", fontWeight:600 }}>Read article →</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}